import React, { useState, useEffect } from "react";
import { Plus, Minus } from "lucide-react";

// Component: Container Input
const ContainerInput = ({
    containerDimensions,
    setContainerDimensions,
    units,
}) => {
    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">
                Container Dimensions
            </h3>
            <div className="grid grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Length ({units})
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={containerDimensions.length}
                        onChange={(e) =>
                            setContainerDimensions((prev) => ({
                                ...prev,
                                length: parseFloat(e.target.value) || 0,
                            }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Length"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Width ({units})
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={containerDimensions.width}
                        onChange={(e) =>
                            setContainerDimensions((prev) => ({
                                ...prev,
                                width: parseFloat(e.target.value) || 0,
                            }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Width"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Height ({units})
                    </label>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={containerDimensions.height}
                        onChange={(e) =>
                            setContainerDimensions((prev) => ({
                                ...prev,
                                height: parseFloat(e.target.value) || 0,
                            }))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Height"
                    />
                </div>
            </div>
        </div>
    );
};

// Component: Crate Input Row with Orientation Labels
const CrateRow = ({ crate, onUpdate, onRemove, canRemove, units }) => {
    const colors = [
        "#3498db",
        "#e74c3c",
        "#f39c12",
        "#9b59b6",
        "#1abc9c",
        "#e67e22",
        "#2ecc71",
        "#34495e",
    ];

    return (
        <div
            className="grid grid-cols-6 gap-3 items-end p-4 bg-gray-50 rounded-lg border-l-4"
            style={{ borderLeftColor: colors[crate.id % colors.length] }}
        >
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Crate Name
                </label>
                <input
                    type="text"
                    value={crate.name}
                    onChange={(e) =>
                        onUpdate({ ...crate, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Crate Type"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Length ({units})
                    <span className="text-xs text-gray-500 block">
                        Base dimension
                    </span>
                </label>
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={crate.length}
                    onChange={(e) =>
                        onUpdate({
                            ...crate,
                            length: parseFloat(e.target.value) || 0,
                        })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width ({units})
                    <span className="text-xs text-gray-500 block">
                        Base dimension
                    </span>
                </label>
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={crate.width}
                    onChange={(e) =>
                        onUpdate({
                            ...crate,
                            width: parseFloat(e.target.value) || 0,
                        })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height ({units})
                    <span className="text-xs text-gray-500 block">
                        Always vertical
                    </span>
                </label>
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={crate.height}
                    onChange={(e) =>
                        onUpdate({
                            ...crate,
                            height: parseFloat(e.target.value) || 0,
                        })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity
                </label>
                <input
                    type="number"
                    min="0"
                    value={crate.quantity}
                    onChange={(e) =>
                        onUpdate({
                            ...crate,
                            quantity: parseInt(e.target.value) || 0,
                        })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                />
            </div>
            <div>
                <button
                    onClick={onRemove}
                    disabled={!canRemove}
                    className={`px-3 py-2 rounded-md font-medium ${
                        canRemove
                            ? "bg-red-500 hover:bg-red-600 text-white"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                    title={
                        canRemove
                            ? "Remove this crate type"
                            : "Cannot remove the last crate type"
                    }
                >
                    <Minus className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

// Component: Crates Input Section
const CratesInput = ({ crates, setCrates, units }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const addCrate = () => {
        const newId = Math.max(...crates.map((c) => c.id), -1) + 1;
        const newCrate = {
            id: newId,
            name: `Crate Type ${String.fromCharCode(65 + newId)}`,
            length: 0,
            width: 0,
            height: 0,
            quantity: 1,
        };
        setCrates([...crates, newCrate]);
    };

    const updateCrate = (updatedCrate) => {
        setCrates(
            crates.map((c) => (c.id === updatedCrate.id ? updatedCrate : c))
        );
    };

    const removeCrate = (id) => {
        if (crates.length > 1) {
            setCrates(crates.filter((c) => c.id !== id));
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-md mb-6">
            <div
                className="bg-blue-500 text-white p-5 rounded-t-lg cursor-pointer flex justify-between items-center hover:bg-blue-600 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span className="font-semibold text-lg">Crate Types</span>
                <span
                    className={`transform transition-transform ${
                        isExpanded ? "rotate-180" : ""
                    }`}
                >
                    ▼
                </span>
            </div>

            {isExpanded && (
                <div className="p-6">
                    <div className="space-y-4 mb-6">
                        {crates.map((crate) => (
                            <CrateRow
                                key={crate.id}
                                crate={crate}
                                onUpdate={updateCrate}
                                onRemove={() => removeCrate(crate.id)}
                                canRemove={crates.length > 1}
                                units={units}
                            />
                        ))}
                    </div>

                    <button
                        onClick={addCrate}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md font-medium transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add Crate Type
                    </button>
                </div>
            )}
        </div>
    );
};

// Component: Results Summary
const ResultsSummary = ({ results }) => {
    const [activeTab, setActiveTab] = useState("summary");

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex border-b border-gray-200 mb-6">
                <button
                    onClick={() => setActiveTab("summary")}
                    className={`px-6 py-3 font-medium rounded-t-lg -mb-px ${
                        activeTab === "summary"
                            ? "bg-blue-500 text-white border-b-2 border-blue-500"
                            : "text-gray-600 hover:text-gray-800"
                    }`}
                >
                    Summary
                </button>
                <button
                    onClick={() => setActiveTab("detailed")}
                    className={`px-6 py-3 font-medium rounded-t-lg -mb-px ml-2 ${
                        activeTab === "detailed"
                            ? "bg-blue-500 text-white border-b-2 border-blue-500"
                            : "text-gray-600 hover:text-gray-800"
                    }`}
                >
                    Detailed
                </button>
            </div>

            {activeTab === "summary" && (
                <div className="space-y-4">
                    <h3 className="text-xl font-semibold mb-4">
                        Packing Summary
                    </h3>

                    <div className="flex justify-between items-center p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                        <span className="font-medium">Containers Needed:</span>
                        <span className="text-xl font-bold text-yellow-700">
                            {results.containersNeeded}
                        </span>
                    </div>

                    <div className="flex justify-between items-center p-4 bg-green-50 border-l-4 border-green-400 rounded">
                        <span className="font-medium">
                            Total Crates Packed:
                        </span>
                        <span className="text-2xl font-bold text-green-700">
                            {results.totalPacked}
                        </span>
                    </div>

                    <div className="flex justify-between items-center p-4 bg-gray-50 border-l-4 border-gray-400 rounded">
                        <span className="font-medium">
                            Total Crates Requested:
                        </span>
                        <span className="text-xl font-bold text-gray-700">
                            {results.totalRequested}
                        </span>
                    </div>

                    <div className="flex justify-between items-center p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
                        <span className="font-medium">
                            Average Utilization:
                        </span>
                        <span className="text-xl font-bold text-blue-700">
                            {results.avgUtilization}%
                        </span>
                    </div>

                    <div className="flex justify-between items-center p-4 bg-red-50 border-l-4 border-red-400 rounded">
                        <span className="font-medium">Unpacked Crates:</span>
                        <span className="text-xl font-bold text-red-700">
                            {results.totalUnpacked}
                        </span>
                    </div>

                    {results.packingEfficiency && (
                        <div className="flex justify-between items-center p-4 bg-purple-50 border-l-4 border-purple-400 rounded">
                            <span className="font-medium">
                                Packing Efficiency:
                            </span>
                            <span className="text-xl font-bold text-purple-700">
                                {results.packingEfficiency}%
                            </span>
                        </div>
                    )}
                </div>
            )}

            {activeTab === "detailed" && (
                <div>
                    <h3 className="text-xl font-semibold mb-4">
                        Detailed Results
                    </h3>
                    {results.containers &&
                        results.containers.map((container) => (
                            <div key={container.id} className="mb-6">
                                <h4 className="text-lg font-semibold mb-3">
                                    Container {container.id} (
                                    {container.utilization}% utilized)
                                </h4>
                                {Object.entries(container.crateTypes || {}).map(
                                    ([name, count]) => (
                                        <div
                                            key={name}
                                            className="flex justify-between items-center p-3 bg-gray-50 border-l-4 border-blue-400 rounded mb-2"
                                        >
                                            <span>{name}</span>
                                            <span className="font-bold">
                                                {count} crates
                                            </span>
                                        </div>
                                    )
                                )}
                            </div>
                        ))}

                    {results.remainingCrates &&
                        results.remainingCrates.length > 0 && (
                            <div>
                                <h4 className="text-lg font-semibold text-red-600 mb-3">
                                    Unpacked Crates
                                </h4>
                                {results.remainingCrates.map((crate) => (
                                    <div
                                        key={crate.name}
                                        className="flex justify-between items-center p-3 bg-red-50 border-l-4 border-red-400 rounded mb-2"
                                    >
                                        <span>{crate.name}</span>
                                        <span className="font-bold">
                                            {crate.quantity} remaining
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                </div>
            )}
        </div>
    );
};

// Component: Individual 3D Container
const Container3D = ({
    container,
    containerDimensions,
    units,
    containerId,
}) => {
    const containerRef = React.useRef(null);
    const sceneRef = React.useRef(null);
    const [rotation, setRotation] = React.useState({ x: -20, y: 30, z: 0 });
    const [scale, setScale] = React.useState(1);
    const [isDragging, setIsDragging] = React.useState(false);
    const [lastMouse, setLastMouse] = React.useState({ x: 0, y: 0 });
    const [showCrates, setShowCrates] = React.useState(true); // New state for crate visibility

    const colors = [
        "#3498db",
        "#e74c3c",
        "#f39c12",
        "#9b59b6",
        "#1abc9c",
        "#e67e22",
        "#2ecc71",
        "#34495e",
    ];

    // Calculate scale for visualization
    const maxDimension = Math.max(
        containerDimensions.length,
        containerDimensions.width,
        containerDimensions.height
    );
    const baseScale = 200 / maxDimension;
    const visualScale = Math.min(baseScale, 2);

    const scaledL = containerDimensions.length * visualScale;
    const scaledW = containerDimensions.width * visualScale;
    const scaledH = containerDimensions.height * visualScale;

    // Mouse event handlers
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setLastMouse({ x: e.clientX, y: e.clientY });
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        const deltaX = e.clientX - lastMouse.x;
        const deltaY = e.clientY - lastMouse.y;

        setRotation((prev) => ({
            ...prev,
            y: prev.y + deltaX * 0.5,
            x: Math.max(-90, Math.min(90, prev.x - deltaY * 0.5)),
        }));

        setLastMouse({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setScale((prev) => Math.max(0.5, Math.min(3, prev * delta)));
    };

    const resetView = () => {
        setRotation({ x: -20, y: 30, z: 0 });
        setScale(1);
    };

    // Add global mouse event listeners
    React.useEffect(() => {
        const handleGlobalMouseMove = (e) => handleMouseMove(e);
        const handleGlobalMouseUp = () => handleMouseUp();

        if (isDragging) {
            document.addEventListener("mousemove", handleGlobalMouseMove);
            document.addEventListener("mouseup", handleGlobalMouseUp);
        }

        return () => {
            document.removeEventListener("mousemove", handleGlobalMouseMove);
            document.removeEventListener("mouseup", handleGlobalMouseUp);
        };
    }, [isDragging, lastMouse]);

    // Create container wireframe as a proper cube
    const createWireframe = () => {
        const faces = [
            // Front face (facing towards viewer)
            {
                name: "front",
                width: scaledL,
                height: scaledH,
                transform: `translateZ(${scaledW / 2}px)`,
                left: `-${scaledL / 2}px`,
                top: `-${scaledH / 2}px`,
            },
            // Back face (facing away from viewer)
            {
                name: "back",
                width: scaledL,
                height: scaledH,
                transform: `translateZ(-${scaledW / 2}px)`,
                left: `-${scaledL / 2}px`,
                top: `-${scaledH / 2}px`,
            },
            // Right face
            {
                name: "right",
                width: scaledW,
                height: scaledH,
                transform: `translateX(${scaledL / 2}px) rotateY(90deg)`,
                left: `-${scaledW / 2}px`,
                top: `-${scaledH / 2}px`,
            },
            // Left face
            {
                name: "left",
                width: scaledW,
                height: scaledH,
                transform: `translateX(-${scaledL / 2}px) rotateY(90deg)`,
                left: `-${scaledW / 2}px`,
                top: `-${scaledH / 2}px`,
            },
            // Top face
            {
                name: "top",
                width: scaledL,
                height: scaledW,
                transform: `translateY(-${scaledH / 2}px) rotateX(90deg)`,
                left: `-${scaledL / 2}px`,
                top: `-${scaledW / 2}px`,
            },
            // Bottom face
            {
                name: "bottom",
                width: scaledL,
                height: scaledW,
                transform: `translateY(${scaledH / 2}px) rotateX(90deg)`,
                left: `-${scaledL / 2}px`,
                top: `-${scaledW / 2}px`,
            },
        ];

        return faces.map((face, index) => (
            <div
                key={face.name}
                style={{
                    position: "absolute",
                    width: `${face.width}px`,
                    height: `${face.height}px`,
                    left: face.left,
                    top: face.top,
                    border: "2px solid #dc3545",
                    background: "rgba(220, 53, 69, 0.1)",
                    opacity: "0.8",
                    transform: face.transform,
                    transformOrigin: "center center",
                    backfaceVisibility: "visible",
                }}
            />
        ));
    };

    // Create 3D crate
    const createCrate = (crate, index) => {
        const crateScaledL = Math.max(12, crate.placedLength * visualScale);
        const crateScaledW = Math.max(12, crate.placedWidth * visualScale);
        const crateScaledH = Math.max(12, crate.placedHeight * visualScale);

        // Fixed positioning - align crates exactly within container bounds
        // Container wireframe extends from -scaledL/2 to +scaledL/2 (same for W and H)
        // Crate positions from algorithm are 0-based, so we need to map them properly
        const posX = -scaledL / 2 + crate.x * visualScale + crateScaledL / 2;
        const posY = scaledH / 2 - crate.z * visualScale - crateScaledH / 2;
        const posZ = -scaledW / 2 + crate.y * visualScale + crateScaledW / 2;

        const crateColor = colors[crate.id % colors.length];

        const faces = [
            {
                name: "front",
                transform: `rotateY(0deg) translateZ(${crateScaledW / 2}px)`,
                brightness: 1.0,
                width: crateScaledL,
                height: crateScaledH,
                left: `-${crateScaledL / 2}px`,
                top: `-${crateScaledH / 2}px`,
            },
            {
                name: "back",
                transform: `rotateY(180deg) translateZ(${crateScaledW / 2}px)`,
                brightness: 0.7,
                width: crateScaledL,
                height: crateScaledH,
                left: `-${crateScaledL / 2}px`,
                top: `-${crateScaledH / 2}px`,
            },
            {
                name: "right",
                transform: `rotateY(90deg) translateZ(${crateScaledL / 2}px)`,
                brightness: 0.9,
                width: crateScaledW,
                height: crateScaledH,
                left: `-${crateScaledW / 2}px`,
                top: `-${crateScaledH / 2}px`,
            },
            {
                name: "left",
                transform: `rotateY(-90deg) translateZ(${crateScaledL / 2}px)`,
                brightness: 0.8,
                width: crateScaledW,
                height: crateScaledH,
                left: `-${crateScaledW / 2}px`,
                top: `-${crateScaledH / 2}px`,
            },
            {
                name: "top",
                transform: `rotateX(90deg) translateZ(${crateScaledH / 2}px)`,
                brightness: 1.3,
                width: crateScaledL,
                height: crateScaledW,
                left: `-${crateScaledL / 2}px`,
                top: `-${crateScaledW / 2}px`,
            },
            {
                name: "bottom",
                transform: `rotateX(-90deg) translateZ(${crateScaledH / 2}px)`,
                brightness: 0.5,
                width: crateScaledL,
                height: crateScaledW,
                left: `-${crateScaledL / 2}px`,
                top: `-${crateScaledW / 2}px`,
            },
        ];

        return (
            <div
                key={`crate-${index}`}
                style={{
                    position: "absolute",
                    transform: `translate3d(${posX}px, ${posY}px, ${posZ}px)`,
                    transformStyle: "preserve-3d",
                }}
            >
                <div
                    style={{
                        position: "relative",
                        transformStyle: "preserve-3d",
                        width: `${crateScaledL}px`,
                        height: `${crateScaledH}px`,
                    }}
                >
                    {faces.map((face) => (
                        <div
                            key={face.name}
                            style={{
                                position: "absolute",
                                width: `${face.width}px`,
                                height: `${face.height}px`,
                                left: face.left,
                                top: face.top,
                                backgroundColor: crateColor,
                                border: "1px solid rgba(0,0,0,0.4)",
                                transform: face.transform,
                                filter: `brightness(${face.brightness})`,
                                backfaceVisibility: "hidden",
                                textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
                                boxShadow: "inset 0 0 10px rgba(0,0,0,0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "10px",
                                fontWeight: "bold",
                                color: "white",
                                transformOrigin: "center center",
                            }}
                            title={`${crate.name} (${crate.placedLength}×${crate.placedWidth}×${crate.placedHeight}) at (${crate.x}, ${crate.y}, ${crate.z})`}
                        >
                            {face.name === "front" &&
                                crateScaledL > 25 &&
                                crateScaledH > 20 &&
                                crate.name.charAt(crate.name.length - 1)}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-center font-semibold text-lg mb-4">
                Container {containerId}
            </div>

            <div
                ref={containerRef}
                className="relative w-full h-96 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-200 flex items-center justify-center cursor-grab overflow-visible"
                style={{
                    perspective: "1200px",
                    cursor: isDragging ? "grabbing" : "grab",
                }}
                onMouseDown={handleMouseDown}
                onWheel={handleWheel}
            >
                {/* Reset button */}
                <button
                    onClick={resetView}
                    className="absolute top-2 left-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 z-10 transition-colors"
                >
                    Reset View
                </button>

                {/* Crate visibility toggle */}
                <div className="absolute top-2 left-24 z-10">
                    <label className="flex items-center gap-2 bg-white bg-opacity-90 px-3 py-1 rounded text-xs cursor-pointer hover:bg-opacity-100 transition-all">
                        <input
                            type="checkbox"
                            checked={showCrates}
                            onChange={(e) => setShowCrates(e.target.checked)}
                            className="w-3 h-3"
                        />
                        <span className="text-gray-700 font-medium">
                            Show Crates
                        </span>
                    </label>
                </div>

                {/* Controls overlay */}
                <div className="absolute top-2 right-2 text-xs text-gray-500 bg-white bg-opacity-90 px-2 py-1 rounded pointer-events-none">
                    Drag to rotate • Scroll to zoom
                </div>

                {/* 3D Scene */}
                <div
                    ref={sceneRef}
                    style={{
                        position: "relative",
                        transformStyle: "preserve-3d",
                        transform: `scale(${scale}) rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) rotateZ(${rotation.z}deg)`,
                        transition: isDragging
                            ? "none"
                            : "transform 0.1s ease-out",
                    }}
                >
                    {/* Container Group */}
                    <div
                        style={{
                            position: "relative",
                            transformStyle: "preserve-3d",
                        }}
                    >
                        {/* Container Wireframe */}
                        <div
                            style={{
                                position: "absolute",
                                transformStyle: "preserve-3d",
                            }}
                        >
                            {createWireframe()}
                        </div>

                        {/* Dimension Labels - Properly spaced around bottom-left-front vertex */}
                        <div
                            style={{
                                position: "absolute",
                                pointerEvents: "none",
                                fontSize: "11px",
                                color: "#495057",
                                fontWeight: "bold",
                            }}
                        >
                            {/* Length label - along bottom edge, positioned below container */}
                            <div
                                style={{
                                    position: "absolute",
                                    left: `-${scaledL / 4}px`,
                                    bottom: `-${scaledH / 2 + 35}px`,
                                    background: "rgba(255, 255, 255, 0.9)",
                                    padding: "2px 8px",
                                    borderRadius: "3px",
                                    border: "1px solid #dee2e6",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {containerDimensions.length} {units}
                            </div>
                            {/* Width label - positioned to the right side, rotated 90 degrees around Z-axis */}
                            <div
                                style={{
                                    position: "absolute",
                                    right: `-${scaledL / 2 + 80}px`,
                                    bottom: `-${scaledH / 2}px`,
                                    background: "rgba(255, 255, 255, 0.9)",
                                    padding: "2px 8px",
                                    borderRadius: "3px",
                                    border: "1px solid #dee2e6",
                                    whiteSpace: "nowrap",
                                    transformOrigin: "center center",
                                }}
                            >
                                {containerDimensions.width} {units}
                            </div>
                            {/* Height label - positioned to the left side */}
                            <div
                                style={{
                                    position: "absolute",
                                    left: `-${scaledL / 2 + 80}px`,
                                    bottom: `-${scaledH / 4}px`,
                                    background: "rgba(255, 255, 255, 0.9)",
                                    padding: "2px 8px",
                                    borderRadius: "3px",
                                    border: "1px solid #dee2e6",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {containerDimensions.height} {units}
                            </div>
                        </div>

                        {/* Packed Crates - Only show if toggle is enabled */}
                        {showCrates &&
                            container.packedCrates &&
                            container.packedCrates.map((crate, index) =>
                                createCrate(crate, index)
                            )}
                    </div>
                </div>
            </div>

            <div className="text-center text-sm text-gray-600 mt-2">
                {container.packedCrates?.length || 0} crates |{" "}
                {container.utilization}% utilized
            </div>
        </div>
    );
};

// Component: Enhanced 3D Visualization
const Visualization3D = ({
    containers,
    containerDimensions,
    units,
    crates,
}) => {
    const colors = [
        "#3498db",
        "#e74c3c",
        "#f39c12",
        "#9b59b6",
        "#1abc9c",
        "#e67e22",
        "#2ecc71",
        "#34495e",
    ];

    if (!containers || containers.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-semibold mb-4">
                    Container Visualization
                </h3>
                <div className="text-center text-gray-500 py-8">
                    <div className="text-6xl mb-4">📦</div>
                    <p>No containers needed or invalid input</p>
                    <p className="text-sm mt-2">
                        Enter valid container dimensions and crate
                        specifications to see the 3D visualization
                    </p>
                </div>
            </div>
        );
    }

    // Filter valid crates for legend
    const validCrates = crates
        ? crates.filter(
              (crate) =>
                  crate.length > 0 &&
                  crate.width > 0 &&
                  crate.height > 0 &&
                  crate.quantity > 0
          )
        : [];

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">
                Container Visualization
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {containers.map((container) => (
                    <Container3D
                        key={container.id}
                        container={container}
                        containerDimensions={containerDimensions}
                        units={units}
                        containerId={container.id}
                    />
                ))}
            </div>

            {/* Legend */}
            {validCrates.length > 0 && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold mb-3">Legend</h4>
                    <div className="flex flex-wrap gap-4">
                        {validCrates.map((crate, index) => (
                            <div
                                key={crate.name}
                                className="flex items-center gap-2"
                            >
                                <div
                                    className="w-5 h-5 rounded border-2 border-white shadow"
                                    style={{
                                        backgroundColor:
                                            colors[index % colors.length],
                                    }}
                                ></div>
                                <span className="text-sm">{crate.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Instructions */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <h5 className="font-medium text-blue-800 mb-2">
                    How to use the 3D visualization:
                </h5>
                <ul className="text-sm text-blue-700 space-y-1">
                    <li>
                        • <strong>Drag</strong> to rotate the container and view
                        from different angles
                    </li>
                    <li>
                        • <strong>Scroll</strong> to zoom in and out for
                        detailed inspection
                    </li>
                    <li>
                        • <strong>Click "Reset View"</strong> to return to the
                        default perspective
                    </li>
                    <li>
                        • <strong>Hover</strong> over crates to see their
                        dimensions and details
                    </li>
                </ul>
            </div>
        </div>
    );
};

// Main App Component
const PackingCalculatorApp = () => {
    // State management
    const [units, setUnits] = useState("cm");
    const [containerDimensions, setContainerDimensions] = useState({
        length: 0,
        width: 0,
        height: 0,
    });
    const [crates, setCrates] = useState([
        {
            id: 0,
            name: "Crate Type A",
            length: 0,
            width: 0,
            height: 0,
            quantity: 1,
        },
    ]);
    const [results, setResults] = useState({
        containersNeeded: 0,
        totalPacked: 0,
        totalRequested: 0,
        avgUtilization: 0,
        totalUnpacked: 0,
        containers: [],
        remainingCrates: [],
    });
    const [errorMessage, setErrorMessage] = useState("");

    // Updated packing algorithm with orientation constraints
    const canCrateFitInSpace = (space, crate) => {
        // Only allow horizontal rotations - crates cannot be flipped on their sides
        // The height dimension always remains as height (vertical)
        const orientations = [
            // Original orientation: length×width×height
            [crate.length, crate.width, crate.height],
            // 90-degree horizontal rotation: width×length×height
            [crate.width, crate.length, crate.height],
        ];

        for (const [l, w, h] of orientations) {
            if (l <= space.length && w <= space.width && h <= space.height) {
                return { length: l, width: w, height: h };
            }
        }
        return null;
    };

    const packCratesInContainer = (
        containerL,
        containerW,
        containerH,
        crateTypes
    ) => {
        const packedCrates = [];
        const remainingCrates = [];

        let availableSpaces = [
            {
                x: 0,
                y: 0,
                z: 0,
                length: containerL,
                width: containerW,
                height: containerH,
            },
        ];

        const sortedCrates = [...crateTypes].sort(
            (a, b) =>
                b.length * b.width * b.height - a.length * a.width * a.height
        );

        for (const crateType of sortedCrates) {
            let remainingQuantity = crateType.quantity;

            while (remainingQuantity > 0 && availableSpaces.length > 0) {
                let placed = false;

                availableSpaces.sort(
                    (a, b) =>
                        b.length * b.width * b.height -
                        a.length * a.width * a.height
                );

                for (let i = 0; i < availableSpaces.length; i++) {
                    const space = availableSpaces[i];
                    const fit = canCrateFitInSpace(space, crateType);

                    if (fit) {
                        packedCrates.push({
                            ...crateType,
                            x: space.x,
                            y: space.y,
                            z: space.z,
                            placedLength: fit.length,
                            placedWidth: fit.width,
                            placedHeight: fit.height,
                        });

                        availableSpaces.splice(i, 1);

                        const newSpaces = [];

                        if (space.x + fit.length < space.x + space.length) {
                            newSpaces.push({
                                x: space.x + fit.length,
                                y: space.y,
                                z: space.z,
                                length: space.length - fit.length,
                                width: space.width,
                                height: space.height,
                            });
                        }

                        if (space.y + fit.width < space.y + space.width) {
                            newSpaces.push({
                                x: space.x,
                                y: space.y + fit.width,
                                z: space.z,
                                length: fit.length,
                                width: space.width - fit.width,
                                height: space.height,
                            });
                        }

                        if (space.z + fit.height < space.z + space.height) {
                            newSpaces.push({
                                x: space.x,
                                y: space.y,
                                z: space.z + fit.height,
                                length: fit.length,
                                width: fit.width,
                                height: space.height - fit.height,
                            });
                        }

                        newSpaces.forEach((newSpace) => {
                            if (
                                newSpace.length > 0 &&
                                newSpace.width > 0 &&
                                newSpace.height > 0
                            ) {
                                availableSpaces.push(newSpace);
                            }
                        });

                        remainingQuantity--;
                        placed = true;
                        break;
                    }
                }

                if (!placed) break;
            }

            if (remainingQuantity > 0) {
                remainingCrates.push({
                    ...crateType,
                    quantity: remainingQuantity,
                });
            }
        }

        return { packedCrates, remainingCrates };
    };

    const calculateContainerUtilization = (
        packedCrates,
        containerL,
        containerW,
        containerH
    ) => {
        const totalVolume = containerL * containerW * containerH;
        const usedVolume = packedCrates.reduce(
            (sum, crate) =>
                sum +
                crate.placedLength * crate.placedWidth * crate.placedHeight,
            0
        );
        return ((usedVolume / totalVolume) * 100).toFixed(1);
    };

    // Enhanced validation for orientation constraints
    const validateCrates = (crates, containerDimensions) => {
        const validCrates = crates.filter(
            (crate) =>
                crate.length > 0 &&
                crate.width > 0 &&
                crate.height > 0 &&
                crate.quantity > 0
        );

        // Check for oversized crates considering only valid orientations
        const oversizedCrates = validCrates.filter((crate) => {
            const orientation1 =
                crate.length <= containerDimensions.length &&
                crate.width <= containerDimensions.width &&
                crate.height <= containerDimensions.height;

            const orientation2 =
                crate.width <= containerDimensions.length &&
                crate.length <= containerDimensions.width &&
                crate.height <= containerDimensions.height;

            // Crate is oversized if it doesn't fit in either valid orientation
            return !orientation1 && !orientation2;
        });

        return {
            validCrates,
            oversizedCrates,
            hasValidCrates: validCrates.length > 0,
            hasOversizedCrates: oversizedCrates.length > 0,
        };
    };

    // Calculate packing whenever inputs change
    useEffect(() => {
        const calculatePacking = () => {
            setErrorMessage("");

            if (
                containerDimensions.length <= 0 ||
                containerDimensions.width <= 0 ||
                containerDimensions.height <= 0
            ) {
                setErrorMessage("Please enter valid container dimensions");
                setResults({
                    containersNeeded: 0,
                    totalPacked: 0,
                    totalRequested: 0,
                    avgUtilization: 0,
                    totalUnpacked: 0,
                    containers: [],
                    remainingCrates: [],
                });
                return;
            }

            const validation = validateCrates(crates, containerDimensions);

            if (!validation.hasValidCrates) {
                setErrorMessage("Please enter at least one valid crate type");
                setResults({
                    containersNeeded: 0,
                    totalPacked: 0,
                    totalRequested: 0,
                    avgUtilization: 0,
                    totalUnpacked: 0,
                    containers: [],
                    remainingCrates: [],
                });
                return;
            }

            if (validation.hasOversizedCrates) {
                const crateNames = validation.oversizedCrates
                    .map((c) => c.name)
                    .join(", ");
                setErrorMessage(
                    `These crates are too large for the container (considering realistic orientations): ${crateNames}`
                );
                setResults({
                    containersNeeded: 0,
                    totalPacked: 0,
                    totalRequested: 0,
                    avgUtilization: 0,
                    totalUnpacked: 0,
                    containers: [],
                    remainingCrates: [],
                });
                return;
            }

            // Pack crates into multiple containers as needed
            const containers = [];
            let remainingCrates = [...validation.validCrates];

            while (remainingCrates.some((c) => c.quantity > 0)) {
                const result = packCratesInContainer(
                    containerDimensions.length,
                    containerDimensions.width,
                    containerDimensions.height,
                    remainingCrates
                );

                if (result.packedCrates.length === 0) {
                    break;
                }

                const crateTypes = {};
                result.packedCrates.forEach((crate) => {
                    if (!crateTypes[crate.name]) {
                        crateTypes[crate.name] = 0;
                    }
                    crateTypes[crate.name]++;
                });

                containers.push({
                    id: containers.length + 1,
                    packedCrates: result.packedCrates,
                    crateTypes,
                    utilization: calculateContainerUtilization(
                        result.packedCrates,
                        containerDimensions.length,
                        containerDimensions.width,
                        containerDimensions.height
                    ),
                });

                remainingCrates = result.remainingCrates;
            }

            const totalPacked = containers.reduce(
                (sum, container) => sum + container.packedCrates.length,
                0
            );
            const totalRequested = validation.validCrates.reduce(
                (sum, crate) => sum + crate.quantity,
                0
            );
            const totalUnpacked = remainingCrates.reduce(
                (sum, crate) => sum + crate.quantity,
                0
            );
            const avgUtilization =
                containers.length > 0
                    ? (
                          containers.reduce(
                              (sum, c) => sum + parseFloat(c.utilization),
                              0
                          ) / containers.length
                      ).toFixed(1)
                    : 0;

            const packingEfficiency =
                totalRequested > 0
                    ? ((totalPacked / totalRequested) * 100).toFixed(1)
                    : 0;

            setResults({
                containersNeeded: containers.length,
                totalPacked,
                totalRequested,
                avgUtilization,
                totalUnpacked,
                packingEfficiency,
                containers,
                remainingCrates,
            });
        };

        calculatePacking();
    }, [containerDimensions, crates]);

    return (
        <div className="min-h-screen bg-gray-100 py-8">
            <div className="max-w-7xl mx-auto px-4">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2">
                        Multi-Crate Container Packing Calculator
                    </h1>
                    <p className="text-gray-600">
                        Optimize packing with 3D space allocation and
                        visualization
                    </p>
                </div>

                {/* Units Selector */}
                <div className="text-center mb-8">
                    <label className="text-lg font-medium text-gray-700 mr-4">
                        Measurement Units:
                    </label>
                    <select
                        value={units}
                        onChange={(e) => setUnits(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="cm">Centimeters (cm)</option>
                        <option value="m">Meters (m)</option>
                        <option value="in">Inches (in)</option>
                        <option value="ft">Feet (ft)</option>
                    </select>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Input Section */}
                    <div className="space-y-6">
                        <ContainerInput
                            containerDimensions={containerDimensions}
                            setContainerDimensions={setContainerDimensions}
                            units={units}
                        />

                        {/* Important Note about Crate Orientation */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <div className="text-blue-500 text-xl">ℹ️</div>
                                <div>
                                    <h4 className="font-semibold text-blue-800 mb-2">
                                        Crate Orientation Constraint
                                    </h4>
                                    <p className="text-blue-700 text-sm leading-relaxed">
                                        Crates are always placed with their{" "}
                                        <strong>width × length</strong> face as
                                        the base. They can be rotated
                                        horizontally (90°) but cannot be flipped
                                        on their sides. The height dimension
                                        always remains vertical for realistic
                                        packing.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <CratesInput
                            crates={crates}
                            setCrates={setCrates}
                            units={units}
                        />

                        {/* Reset Button */}
                        <div className="text-center">
                            <button
                                onClick={() => {
                                    setContainerDimensions({
                                        length: 0,
                                        width: 0,
                                        height: 0,
                                    });
                                    setCrates([
                                        {
                                            id: 0,
                                            name: "Crate Type A",
                                            length: 0,
                                            width: 0,
                                            height: 0,
                                            quantity: 1,
                                        },
                                    ]);
                                    setResults({
                                        containersNeeded: 0,
                                        totalPacked: 0,
                                        totalRequested: 0,
                                        avgUtilization: 0,
                                        totalUnpacked: 0,
                                        containers: [],
                                        remainingCrates: [],
                                    });
                                    setErrorMessage("");
                                }}
                                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-medium transition-colors"
                            >
                                Reset All
                            </button>
                        </div>
                    </div>

                    {/* Results Section */}
                    <div>
                        <ResultsSummary results={results} />
                        {errorMessage && (
                            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-700 font-medium">
                                    {errorMessage}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Visualization Section */}
                <Visualization3D
                    containers={results.containers}
                    containerDimensions={containerDimensions}
                    units={units}
                    crates={crates}
                />

                {/* Footer */}
                <div className="text-center mt-12 text-gray-600">
                    <p>
                        © 2024 Multi-Crate Packing Calculator. Built with React
                        and Tailwind CSS.
                    </p>
                    <p className="text-sm mt-2">
                        Realistic packing simulation with proper crate
                        orientation constraints.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PackingCalculatorApp;
